/**
 * Player customization catalogs (APIv2 `/player-assets`).
 *
 * Avatars, Player Backgrounds, and App Music are all the same `player asset`
 * entity, distinguished by `type`. This map drives the three admin screens
 * (list + add/edit) so they can share one set of components instead of
 * three near-duplicate ones.
 */

export const PLAYER_ASSET_TYPES = {
  avatar: {
    type: "avatar",
    basePath: "/player-avatars",
    label: "Player Avatars",
    singular: "Avatar",
    fields: {
      image: {
        show: true,
        required: false,
        label: "Image",
        helperText: 'Optional — leave empty for the "No Avatar" option.',
      },
      thumbnail: { show: true, required: false, label: "Thumbnail" },
      wideImage: { show: false },
      audio: { show: false },
    },
  },
  background: {
    type: "background",
    basePath: "/player-backgrounds",
    label: "Player Backgrounds",
    singular: "Background",
    fields: {
      image: { show: true, required: true, label: "Portrait Image" },
      wideImage: { show: true, required: false, label: "Landscape Image" },
      thumbnail: { show: false },
      audio: { show: false },
    },
  },
  background_music: {
    type: "background_music",
    basePath: "/player-music",
    label: "App Music",
    singular: "App Music",
    fields: {
      audio: { show: true, required: true, label: "Audio File" },
      image: { show: true, required: false, label: "Tile Image" },
      thumbnail: { show: false },
      wideImage: { show: false },
    },
  },
};

export const PLAYER_ASSET_TYPES_LIST = Object.values(PLAYER_ASSET_TYPES);

export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const SLUG_MAX_LENGTH = 64;
export const NAME_MAX_LENGTH = 100;
export const ANALYTICS_NAME_MAX_LENGTH = 100;

// Best-effort suggestion only — the admin can still edit it before create.
export function slugify(value) {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");
}

// The fields an add/edit form can send, in the shape the API expects. Sort
// order is never sent from this form: the admin sets it by dragging rows on
// the list page (see PlayerAssetsTable + reorderPlayerAssets), so an edit
// must never move an asset and a create must always append at the end.
export function toPayload(state) {
  return {
    name: state.name,
    analytics_name: state.analytics_name,
    is_active: state.is_active,
    is_default: state.is_default,
    image_url: state.image_url || null,
    thumbnail_url: state.thumbnail_url || null,
    wide_image_url: state.wide_image_url || null,
    audio_url: state.audio_url || null,
  };
}

// Only the fields that differ from what was loaded. An edit then never sends a
// value the admin did not touch, so a field the server did not return (or the
// form does not show for this type) can never be cleared by a save.
export function changedFields(current, original) {
  return Object.fromEntries(Object.entries(current).filter(([key, value]) => value !== original[key]));
}

// Shown when a request fails without a response (offline, timeout, lost
// response). The shared error handler only reports server responses, and in
// this case the admin cannot know whether the change was applied.
export const UNCONFIRMED_MESSAGE =
  "The server did not answer, so this change could not be confirmed. Check your connection and reload the list before trying again.";
